import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, NavLink } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import LoadingSpinner from '../components/LoadingSpinner';
import ServiceCallDetailsModal from '../components/ServiceCallDetailsModal';
import InspectionDetailsModal from '../components/InspectionDetailsModal';
import WipSchedulerDetailsModal from '../components/WipSchedulerDetailsModal';
import OperationSchedulerDetailModal from '../components/OperationSchedulerDetailModal';
import { DocumentCheckIcon, DocumentTextIcon, EyeIcon, VideoCameraIcon } from '@heroicons/react/24/outline';

const EventSearchPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [selectedWipJob, setSelectedWipJob] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isInspectionDetailModalOpen, setIsInspectionDetailModalOpen] = useState(false);
  const [isWipDetailModalOpen, setIsWipDetailModalOpen] = useState(false);
  const [isOperationDetailModalOpen, setIsOperationDetailModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;    

  const [filters, setFilters] = useState({
    query: '',
    startDate: '',
    endDate: '',
    eventType: 'all',
    tech_id: '',
    department: '',
  });

  const handleOpenDetails = async (row) => {
    try {
      setLoading(true);

      const res = await axios.get(`${apiUrl}/scheduler_crud.php`, {
        params: {
          getEventDetail: 1,
          type: row.type,
          id: row.id,
        },
      });

      const fullRow = res.data;

      if (!fullRow || fullRow.error) {
        toast.error(fullRow?.error || 'Event not found.');
        return;
      }

      if (row.type === 'service') {
        setSelectedServiceCall(fullRow);
        setIsDetailModalOpen(true);
        return;
      }

      if (row.type === 'inspection') {
        setSelectedInspection(fullRow);
        setIsInspectionDetailModalOpen(true);
        return;
      }

      if (row.type === 'wip') {
        setSelectedWipJob(fullRow);
        setIsWipDetailModalOpen(true);
        return;
      }

      if (row.type === 'operations') {
        setSelectedOperation(fullRow);
        setIsOperationDetailModalOpen(true);
        return;
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('Access denied.', { position: 'top-center' });
      } else {
        toast.error('Could not load event details.', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${apiUrl}/service_crud.php?employee_list=true`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      });
      setEmployees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: 'top-center',
        });
      } else {
        toast.error('Error loading employees.', { position: 'top-center' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();

    if (!filters.query && !filters.startDate && !filters.endDate && !filters.tech_id && filters.eventType === 'all') {
      toast.error('Enter at least one filter before searching.', {
        position: 'top-center',
      });
      return;
    }

    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      toast.error('Start date cannot be later than end date.', {
        position: 'top-center',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/scheduler_crud.php`, {
        params: {
          searchUnifiedEvents: 1,
          query: filters.query,
          startDate: filters.startDate,
          endDate: filters.endDate,
          eventType: filters.eventType,
          tech_id: filters.tech_id,
          department: filters.department,
        },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      setResults(rows);
      setCurrentPage(1);
      if (rows.length === 0) {
        toast.info('No events found.', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('Access denied.', { position: 'top-center' });
      } else {
        toast.error('Error searching events.', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters({
      query: '',
      startDate: '',
      endDate: '',
      eventType: 'all',
      tech_id: '',
      department: '',
    });
    setResults([]);
    setCurrentPage(1);
  };

  const filteredEmployees = useMemo(() => {
    if (!filters.department) return employees;
    if (filters.department === 'Field') {
      return employees.filter(e => String(e.o_f_type).toLowerCase() === 'field');
    }
    return employees.filter(e => e.department === filters.department);
  }, [employees, filters.department]);

  const totalRecords = results.length;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const paginatedResults = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return results.slice(start, start + pageSize);
  }, [results, currentPage]);

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };  

  const goToSchedulerDay = (dateStr) => {
    navigate(`/full_scheduler_viewer?date=${dateStr}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',   // Fri
      month: 'short',     // Jun
      day: 'numeric',     // 13
      year: 'numeric',    // 2025
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const date = new Date();
    date.setHours(h);
    date.setMinutes(m);

    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };  

  return (
    <div className="w-[90vw] mx-auto mt-8">
      <HeadingCp label="Search Events" />

      <div className="mb-4">
        <NavLink to="/full_scheduler_viewer">
          <button
            className="px-4 py-2 rounded-md bg-slate-600 text-white hover:bg-slate-700"
            type="button"
          >
            Back to Scheduler
          </button>
        </NavLink>
      </div>

      <form onSubmit={handleSearch} className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <input
            type="text"
            name="query"
            value={filters.query}
            onChange={handleChange}
            placeholder="Search address, customer, description, ID..."
            className="border rounded-md px-3 py-2"
          />

          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleChange}
            className="border rounded-md px-3 py-2"
          />

          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleChange}
            className="border rounded-md px-3 py-2"
          />

          <select
            name="eventType"
            value={filters.eventType}
            onChange={handleChange}
            className="border rounded-md px-3 py-2"
          >
            <option value="all">All Types</option>
            <option value="service">Service</option>
            <option value="inspection">Inspection</option>
            <option value="wip">WIP</option>
            <option value="operations">Operations</option>
            <option value="OFF">OFF</option>
          </select>

          <select
            name="department"
            value={filters.department}
            onChange={handleChange}
            className="border rounded-md px-3 py-2"
          >
            <option value="">All Depts</option>
            <option value="Field">Field</option>
            <option value="Service">Service</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Install">Install</option>
            <option value="Admin">Admin</option>
            <option value="Purchasing">Purchasing</option>
          </select>

          <select
            name="tech_id"
            value={filters.tech_id}
            onChange={handleChange}
            className="border rounded-md px-3 py-2"
          >
            <option value="">All Techs</option>
            {filteredEmployees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            type="submit"
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 rounded-md bg-orange-600 text-white hover:bg-orange-700"
          >
            Reset
          </button>
        </div>
      </form>
        {results.length > 0 && !loading && (
          <div className="mb-3 flex justify-between items-center">
            <div className="text-sm font-medium text-gray-700">
              Total Records: <span className="font-bold">{totalRecords}</span>
            </div>
            <div className="text-sm text-gray-500">
              Page {currentPage} of {totalPages || 1}
            </div>
          </div>
        )}
      {loading ? (
        
        <LoadingSpinner children="Searching events..." />
        
      ) : (
        
        <div className="overflow-auto bg-white shadow rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Date</th>
                {/* <th className="text-left px-4 py-3">Time</th> */}
                <th className="text-left px-4 py-3">Tech</th>
                <th className="text-left px-4 py-3">Customer / Site</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">View</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-6 text-center text-gray-500">
                    No results yet.
                  </td>
                </tr>
              ) : (
                paginatedResults.map((row, idx) => (
                  <tr key={`${row.type}-${row.id || idx}`} className="border-t">
                    <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold text-white ${
                        row.type === 'service'
                          ? 'bg-blue-600'
                          : row.type === 'inspection'
                          ? 'bg-green-600'
                          : row.type === 'wip'
                          ? 'bg-purple-600'
                          : row.type === 'operations'
                          ? 'bg-lime-500 text-gray-900'
                          : 'bg-gray-400'
                      }`}
                    >
                      {row.type.toUpperCase()}
                    </span>
                  </td>
                    <td className="px-4 py-3 min-w-[150px]">
                      <div className="font-medium">
                        {formatDate(row.date)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatTime(row.start_time)} {row.end_time && `- ${formatTime(row.end_time)}`}
                      </div>
                    </td>
                    {/* <td className="px-4 py-3">
                      {row.start_time || ''} {row.end_time ? `- ${row.end_time}` : ''}
                    </td> */}
                    <td className="px-4 py-3">{row.tech_name || ''}</td>
                    {/* <td className="px-4 py-3"><span className='text-sm text-gray-700'>{row.customer}</span><br/>{row.job_site || ''}</td> */}
                    <td className="px-4 py-3 w-[350px] min-w-[250px] max-w-[600px]">
                      <div className="truncate">
                        <span className='text-sm text-gray-700'>{row.customer}</span><br/>{row.job_site || ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">{row.description || ''}</td>

                  <td className="px-4 py-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleOpenDetails(row)}
                    >
                      <EyeIcon className="h-5 w-5 text-green-800" />
                    </button>

                    {(row.status.toLowerCase() === 'completed') && (
                      <>
                        {row.type === 'service' ? (
                          <a href={`/service_ticket/${row.id}`} target="_blank" rel="noopener noreferrer">
                            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                          </a>
                        ) : row.type === 'inspection' ? (
                          <a href={`/maintenance_ticket/${row.id}`} target="_blank" rel="noopener noreferrer">
                            <DocumentTextIcon className="h-5 w-5 text-green-600" />
                          </a>
                        ) : row.type === 'wip' ? (
                          <a href={`/wip_report/${row.id}`} target="_blank" rel="noopener noreferrer">
                            <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                          </a>
                        ) : row.type === 'operations' ? (
                          <a href={`/opreport/${row.id}`} target="_blank" rel="noopener noreferrer">
                            <DocumentTextIcon className="h-5 w-5 text-lime-500" />
                          </a>
                        ) : null}
                      </>
                    )}
                  </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="p-4 bg-slate-100 text-white rounded-b-lg">
            {results.length > 0 && !loading && (
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * pageSize + 1}
                  {' - '}
                  {Math.min(currentPage * pageSize, totalRecords)}
                  {' of '}
                  {totalRecords}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-md bg-slate-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <button
                    type="button"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="px-4 py-2 rounded-md bg-slate-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}          
            </div>
        </div>
      )}

      <ServiceCallDetailsModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedServiceCall(null);
        }}
        serviceCall={selectedServiceCall}
        onEdit={() => {}}
        edit={false}
      />

      <InspectionDetailsModal
        isOpen={isInspectionDetailModalOpen}
        onClose={() => {
          setIsInspectionDetailModalOpen(false);
          setSelectedInspection(null);
        }}
        inspection={selectedInspection}
        onEdit={() => {}}
        edit={false}
      />

      <WipSchedulerDetailsModal
        isOpen={isWipDetailModalOpen}
        onClose={() => {
          setIsWipDetailModalOpen(false);
          setSelectedWipJob(null);
        }}
        inspection={selectedWipJob}
        onEdit={() => {}}
        edit={false}
      />

      <OperationSchedulerDetailModal
        isOpen={isOperationDetailModalOpen}
        onClose={() => {
          setIsOperationDetailModalOpen(false);
          setSelectedOperation(null);
        }}
        operation={selectedOperation}
        onEdit={() => {}}
        edit={false}
      />   

    </div>
  );
};

export default EventSearchPage;